import type {
  StoryRefreshScheduleOutApi,
  StoryRefreshScheduleResponseApi,
  StoryRefreshScheduleWriteRequestApi,
} from "./storySchedule.types";

/**
 * In-memory stand-in for `/api/v1/workspaces/{id}/stories/{id}/refresh-schedule`.
 *
 * Speaks the real wire contract — snake_case variants in, a
 * `StoryRefreshScheduleResponseApi` out — so swapping in the live client is a
 * change of transport only. It deliberately mimics the endpoint's
 * create-vs-replace split rather than upserting: `POST` rejects when a schedule
 * already exists and `PUT` rejects when none does, which is exactly the pair of
 * mistakes we want to catch before hitting the backend.
 */

const schedules = new Map<string, StoryRefreshScheduleOutApi>();

const scheduleKey = (workspaceId: string, storyId: string): string =>
  `${workspaceId}:${storyId}`;

const now = (): string => new Date().toISOString();

const respond = (
  storyId: string,
  schedule: StoryRefreshScheduleOutApi | null,
): StoryRefreshScheduleResponseApi => ({ story_id: storyId, schedule });

/** GET — `schedule: null` when this story has no schedule yet. */
export const getMockStoryRefreshSchedule = async (
  workspaceId: string,
  storyId: string,
): Promise<StoryRefreshScheduleResponseApi> =>
  respond(storyId, schedules.get(scheduleKey(workspaceId, storyId)) ?? null);

/** POST — creates. Mirrors the endpoint's 409 when a schedule already exists. */
export const createMockStoryRefreshSchedule = async (
  workspaceId: string,
  storyId: string,
  request: StoryRefreshScheduleWriteRequestApi,
): Promise<StoryRefreshScheduleResponseApi> => {
  const key = scheduleKey(workspaceId, storyId);
  if (schedules.has(key)) {
    throw new Error("A schedule already exists for this story.");
  }

  const timestamp = now();
  const created = {
    ...request.schedule,
    id: `story-refresh-schedule:${key}`,
    // Real `next_run_at` is computed server-side; the mock has no scheduler.
    next_run_at: null,
    created_at: timestamp,
    updated_at: timestamp,
  } as StoryRefreshScheduleOutApi;

  schedules.set(key, created);
  return respond(storyId, created);
};

/**
 * PUT — full replace, not a patch. Every field the new `repeat` doesn't use is
 * dropped rather than carried over, and `updated_at` bumps; `id` and
 * `created_at` survive. Mirrors the endpoint's 404 when no schedule exists.
 */
export const replaceMockStoryRefreshSchedule = async (
  workspaceId: string,
  storyId: string,
  request: StoryRefreshScheduleWriteRequestApi,
): Promise<StoryRefreshScheduleResponseApi> => {
  const key = scheduleKey(workspaceId, storyId);
  const existing = schedules.get(key);
  if (!existing) {
    throw new Error("No schedule exists for this story yet.");
  }

  const replaced = {
    ...request.schedule,
    id: existing.id,
    next_run_at: null,
    created_at: existing.created_at,
    updated_at: now(),
  } as StoryRefreshScheduleOutApi;

  schedules.set(key, replaced);
  return respond(storyId, replaced);
};

/**
 * Convenience for callers that just want the schedule persisted and don't yet
 * track whether one exists — picks create or replace the same way the real
 * integration will, off the presence of an existing schedule.
 */
export const saveMockStoryRefreshSchedule = async (
  workspaceId: string,
  storyId: string,
  request: StoryRefreshScheduleWriteRequestApi,
): Promise<StoryRefreshScheduleResponseApi> =>
  schedules.has(scheduleKey(workspaceId, storyId))
    ? replaceMockStoryRefreshSchedule(workspaceId, storyId, request)
    : createMockStoryRefreshSchedule(workspaceId, storyId, request);

/** Test-only cleanup for the module-scoped mock store. */
export const resetMockStoryRefreshSchedules = (): void => {
  schedules.clear();
};
